import React from 'react';
import {
  RACES, UNITS, ECON, mineralIncome, gasIncome, unitsPerMinute, formatNum,
} from './sc2Data';

// Income of one fully worked base (16 minerals + 6 gas workers).
const BASE_MIN = mineralIncome(16, 1); // ~928
const BASE_GAS = gasIncome(6, 1); // ~327

function drainPerFacility(unitId, opts) {
  const u = UNITS[unitId];
  const rate = unitsPerMinute(u, opts);
  return { minerals: rate * u.minerals, gas: rate * u.gas, rate };
}

function FacilityRow({ label, unitId, opts, income }) {
  const d = drainPerFacility(unitId, opts);
  const sustainable = Math.floor(income.minerals / d.minerals * 10) / 10;
  const gasLimited = d.gas > 0 ? Math.floor(income.gas / d.gas * 10) / 10 : null;
  const limit = gasLimited !== null ? Math.min(sustainable, gasLimited) : sustainable;
  return (
    <tr>
      <td>{label}</td>
      <td>{formatNum(d.rate, 1)}/мин</td>
      <td>💎 {formatNum(d.minerals)} {d.gas > 0 ? `· 🟢 ${formatNum(d.gas)}` : ''}</td>
      <td><strong>≈ {formatNum(limit, 1)}</strong></td>
    </tr>
  );
}

const GUIDE = {
  terran: {
    rules: [
      'Стройте КСМ без остановки из всех командных центров до ~70–80 рабочих.',
      'Правило «3 казармы на базу» для чистого био на минералах (морпехи с реакторами).',
      'Стандарт на 2 базах: 5 казарм + 1 завод + 1 космопорт ≈ полная загрузка дохода.',
      'Каждая орбиталка = ~160 минералов/мин бесплатно (MULE). Не забывайте кидать мулов!',
      'Реактор удваивает производство дешёвых юнитов — ставьте на морпехов, геллионов, медэваков.',
      'Копятся минералы? → ещё казарма или командный центр. Копится газ? → больше мародёров/танков/медэваков.',
    ],
    facilities: [
      { label: 'Казармы + реактор (морпехи)', unitId: 'marine', opts: { reactor: true } },
      { label: 'Казармы + лаборатория (мародёры)', unitId: 'marauder', opts: {} },
      { label: 'Завод + лаборатория (танки)', unitId: 'siege_tank', opts: {} },
      { label: 'Космопорт + реактор (медэваки)', unitId: 'medivac', opts: { reactor: true } },
    ],
  },
  protoss: {
    rules: [
      'Стройте зондов без остановки из всех нексусов до ~70–80 рабочих.',
      'Правило «4 гейта на базу» для гейтвей-армии (после исследования варп-врат).',
      'Стандарт на 2 базах: 7–8 гейтов, либо 4–5 гейтов + 2 робо/старгейта.',
      'Варп-врата сокращают цикл: сталкер 27с → варп каждые 20с (+35% производства).',
      'Хронобуст нексуса ускоряет производство на 50% — используйте на зондов, потом на ключевые юниты/технологии.',
      'Копятся минералы? → ещё гейт или нексус. Копится газ? → больше темпларов/бессмертных/колоссов.',
    ],
    facilities: [
      { label: 'Варп-врата (зилоты)', unitId: 'zealot', opts: { warpgate: true } },
      { label: 'Варп-врата (сталкеры)', unitId: 'stalker', opts: { warpgate: true } },
      { label: 'Робо-фабрика (бессмертные)', unitId: 'immortal', opts: {} },
      { label: 'Звёздные врата (фениксы)', unitId: 'phoenix', opts: {} },
    ],
  },
  zerg: {
    rules: [
      'Продакшен зерга = личинки. Хатчери даёт ~5,5 личинок/мин, королева с инжектом +~5,7.',
      '1 королева на каждую хатчу — инжект каждые 29 секунд, не пропускайте!',
      'Дроны и армия конкурируют за личинки: сначала экономика (до ~66–75 дронов), потом армия.',
      'Не хватает личинок при полном банке? → макро-хатчери (хатча в основной базе только ради личинок).',
      'Правило лимита: владыка каждые ~8 потраченных единиц лимита. Заказывайте заранее!',
      'Копится газ? → больше гидр/люркеров/муталисков. Копятся минералы? → зерглинги, хатчери, споры.',
    ],
    facilities: [],
  },
};

function Guide({ raceId }) {
  const race = RACES[raceId];
  const g = GUIDE[raceId];
  const income = { minerals: BASE_MIN + (raceId === 'terran' ? ECON.MULE_PER_MIN : 0), gas: BASE_GAS };

  const larvaePerBase = ECON.LARVA_NATURAL_PER_MIN + ECON.LARVA_INJECT_PER_MIN;

  return (
    <div>
      <h2 className="sc2-section-title" style={{ color: race.color }}>
        {race.icon} Сколько нужно продакшена: {race.ru}
      </h2>

      <div className="sc2-two-col">
        <div className="sc2-panel">
          <h3>Главные правила</h3>
          <ol className="sc2-rules">
            {g.rules.map((r, i) => <li key={i}>{r}</li>)}
          </ol>
          <div className="sc2-hint-box">
            💡 Универсальный ориентир: <strong>полная база ≈ 💎 {formatNum(BASE_MIN)} + 🟢 {formatNum(BASE_GAS)} в минуту</strong> (16 рабочих на минералах + 6 на газе).
            Ваша цель — чтобы производственные здания «съедали» весь этот доход без простоя.
          </div>
        </div>

        <div className="sc2-panel">
          {raceId !== 'zerg' ? (
            <>
              <h3>Сколько зданий кормит одна база</h3>
              <table className="sc2-table compact">
                <thead>
                  <tr>
                    <th>Здание (юнит)</th>
                    <th>Юнитов</th>
                    <th>Расход/мин</th>
                    <th>Зданий на базу</th>
                  </tr>
                </thead>
                <tbody>
                  {g.facilities.map((f) => (
                    <FacilityRow key={f.label} {...f} income={income} />
                  ))}
                </tbody>
              </table>
              <p className="sc2-note">
                «Зданий на базу» — сколько таких зданий может непрерывно работать с дохода одной полной базы
                (учтён и газовый лимит). Для смешанной армии комбинируйте: сумма расходов ≤ доход.
              </p>
            </>
          ) : (
            <>
              <h3>Личинки: ваш продакшен</h3>
              <table className="sc2-table compact">
                <tbody>
                  <tr><td>Хатчери (естественный спавн)</td><td>~{formatNum(ECON.LARVA_NATURAL_PER_MIN, 1)} личинок/мин</td></tr>
                  <tr><td>+ Королева с инжектом</td><td>+~{formatNum(ECON.LARVA_INJECT_PER_MIN, 1)} личинок/мин</td></tr>
                  <tr><td><strong>Итого база с королевой</strong></td><td><strong>~{formatNum(larvaePerBase, 1)} личинок/мин</strong></td></tr>
                  <tr><td>Это зерглингов (х2 с личинки)</td><td>~{formatNum(larvaePerBase * 2, 0)}/мин (💎 {formatNum(larvaePerBase * 2 * 25)})</td></tr>
                  <tr><td>Или тараканов</td><td>~{formatNum(larvaePerBase, 0)}/мин (💎 {formatNum(larvaePerBase * 75)} · 🟢 {formatNum(larvaePerBase * 25)})</td></tr>
                  <tr><td>Или дронов</td><td>~{formatNum(larvaePerBase, 0)}/мин</td></tr>
                </tbody>
              </table>
              <p className="sc2-note">
                Одна база с инжектом производит юнитов на ~{formatNum(larvaePerBase * 75)} минералов/мин (тараканы) —
                больше, чем база добывает! Поэтому у зерга узкое место чаще деньги, а личинок не хватает
                только на дешёвой армии (лингах) — тогда стройте макро-хатчери.
              </p>
            </>
          )}
        </div>
      </div>

      <div className="sc2-panel">
        <h3>Как думать о продакшене (для любой расы)</h3>
        <div className="sc2-steps">
          <div className="sc2-step"><span className="sc2-step-num">1</span> Посчитайте доход: вкладка «Добыча» (базы × рабочие).</div>
          <div className="sc2-step"><span className="sc2-step-num">2</span> Выберите состав армии: вкладка «Продакшен» — добавьте юниты.</div>
          <div className="sc2-step"><span className="sc2-step-num">3</span> Подгоните число зданий так, чтобы расход был 80–100% дохода.</div>
          <div className="sc2-step"><span className="sc2-step-num">4</span> Проверьте цепочку технологий: вкладка «Цепочка постройки».</div>
          <div className="sc2-step"><span className="sc2-step-num">5</span> В игре: минералы &gt; 500 и растут? Добавьте здание/базу. Продакшен простаивает? Добавьте рабочих.</div>
        </div>
      </div>
    </div>
  );
}

export default Guide;
